import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import apiUrl from "../../../Apiurl";
import { BlockSkeleton, TextSkeleton } from "../../../interfaccia/loading";

const STATI_FATTURA = {
  DA_EMETTERE: "Da emettere",
  DA_CONTROLLARE: "Spedizioni da controllare",
  INCOMBENTE: "Fattura incombente",
};

function dataLeggibile(value) {
  return value ? new Date(value).toLocaleString("it-IT") : "—";
}

function Fatture({ idSped, utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [erroreInvio, setErroreInvio] = useState("");
  const [invio, setInvio] = useState(false);
  const [confermaAnnullamento, setConfermaAnnullamento] = useState(false);
  const richiestaRef = useRef(null);
  const invioRef = useRef(false);
  const canUpdateLogistica = Boolean(utente?.permessiAPI?.["update:logistica"]);
  const idValido = /^\d+$/.test(String(idSped)) && Number(idSped) > 0;

  useEffect(() => {
    const controller = new AbortController();
    richiestaRef.current = controller;
    invioRef.current = false;
    setDati(null);
    setErrore("");
    setErroreInvio("");
    setInvio(false);
    setConfermaAnnullamento(false);
    setLoading(idValido);
    const carica = async () => {
      try {
        const token = await getAccessTokenSilently();
        if (controller.signal.aborted) return;
        const response = await axios.get(`${apiUrl}/logistica/fatture/ordine/${idSped}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!controller.signal.aborted && richiestaRef.current === controller) setDati(response.data);
      } catch (error) {
        if (controller.signal.aborted || richiestaRef.current !== controller || axios.isCancel(error)) return;
        setErrore(error.response?.status === 404
          ? "Spedizione non trovata"
          : error.response?.data?.error || "Impossibile caricare i dati della fattura.");
      } finally {
        if (!controller.signal.aborted && richiestaRef.current === controller) setLoading(false);
      }
    };
    if (idValido) carica();
    return () => controller.abort();
  }, [idSped, idValido, getAccessTokenSilently]);

  const aggiornaEmissione = async (emessa) => {
    if (!canUpdateLogistica || invioRef.current) return;
    const username = utente?.username?.trim();
    if (!username) {
      setErroreInvio("Username Domino mancante: impossibile aggiornare l'emissione della fattura.");
      return;
    }
    const richiesta = richiestaRef.current;
    const corrente = () => richiestaRef.current === richiesta && !richiesta.signal.aborted;
    invioRef.current = true;
    setInvio(true);
    setErroreInvio("");
    try {
      const token = await getAccessTokenSilently();
      // La scrittura già partita non viene annullata cambiando spedizione o tab.
      if (!corrente()) return;
      const response = await axios.post(`${apiUrl}/logistica/fatture/ordine/${idSped}/emessa`,
        { emessa, username }, { headers: { Authorization: `Bearer ${token}` } });
      if (corrente()) {
        setDati(response.data);
        setConfermaAnnullamento(false);
      }
    } catch (error) {
      if (corrente()) setErroreInvio(error.response?.data?.error || "Impossibile aggiornare l'emissione della fattura.");
    } finally {
      if (corrente()) {
        invioRef.current = false;
        setInvio(false);
      }
    }
  };

  return (
    <div className="sezioneConScroll h-100 w-100 pa3">
      <h2 className="mt0">Fatture</h2>
      {loading ? <div role="status" aria-label="Caricamento fattura" aria-busy="true">
        <TextSkeleton lines={5} className="mb3" />
        <BlockSkeleton height={160} />
      </div> : errore ? <div role="alert">{errore}</div>
        : !idValido ? <p>Salva la spedizione prima di consultare la fattura.</p>
          : dati?.applicabile === false ? <p>Ordine senza richiesta di fattura</p>
            : dati && <>
              <p>Ordine: <strong>{dati.fonte} / {dati.nRifPrestaShop}</strong></p>
              <p>Stato fattura: <strong>{STATI_FATTURA[dati.stato] || dati.stato}</strong></p>
              <dl>
                <dt>Richiesta inviata il</dt>
                <dd className="ml0 mb3">{dati.richiestaInviataIl ? dataLeggibile(dati.richiestaInviataIl) : "Non ancora inviata"}</dd>
                <dt>Controllo comunicato il</dt>
                <dd className="ml0 mb3">{dataLeggibile(dati.controlloInviatoIl)}</dd>
                <dt>Fattura emessa il</dt>
                <dd className="ml0 mb3">{dati.fatturaEmessaIl ? dataLeggibile(dati.fatturaEmessaIl) : "Non emessa"}</dd>
                <dt>Fattura emessa da</dt>
                <dd className="ml0 mb3">{dati.fatturaEmessaDa || "—"}</dd>
              </dl>
              {canUpdateLogistica && <div className="mb3">
                {dati.fatturaEmessaIl ? <>
                  <button type="button" className="tastoInputOrizzontale br3" disabled={invio}
                    onClick={() => setConfermaAnnullamento(true)}>Annulla emissione</button>
                  {confermaAnnullamento && <div role="region" aria-label="Conferma annullamento emissione" className="mt3 pa3 ba bordoColAlt br3">
                    <p>Vuoi annullare il contrassegno di fattura emessa per questo ordine?</p>
                    <button type="button" className="tastoInputOrizzontale br3" disabled={invio}
                      onClick={() => aggiornaEmissione(false)}>Conferma annullamento</button>
                    <button type="button" className="tastoInputOrizzontale br3" disabled={invio}
                      onClick={() => setConfermaAnnullamento(false)}>Mantieni emissione</button>
                  </div>}
                </> : <button type="button" className="tastoInputOrizzontale br3" disabled={invio}
                  onClick={() => aggiornaEmissione(true)}>Segna fattura come emessa</button>}
              </div>}
              {erroreInvio && <div role="alert" className="mb3">{erroreInvio}</div>}
              <h3>Spedizioni coinvolte</h3>
              <div className="overflow-auto">
                <table className="w-100 collapse">
                  <thead><tr>
                    <th className="pa2 tl">ID spedizione</th><th className="pa2 tl">Stato ordine</th>
                    <th className="pa2 tl">Data partenza</th><th className="pa2 tl">Emissione scontrino</th>
                  </tr></thead>
                  <tbody>{(dati.spedizioni || []).map(spedizione => <tr key={spedizione.idSped}>
                    <td className="pa2">{spedizione.idSped}</td>
                    <td className="pa2">{spedizione.Stato_Ordine || "—"}</td>
                    <td className="pa2">{spedizione.Data_partenza ? new Date(spedizione.Data_partenza).toLocaleDateString("it-IT") : "—"}</td>
                    <td className="pa2">{spedizione.Emissione_scontrino || "—"}</td>
                  </tr>)}</tbody>
                </table>
              </div>
            </>}
    </div>
  );
}

export default Fatture;
